/**
 * 申請一覧(v3.7)。
 * - 管理者・経理は全事業部の申請を1つの一覧で表示(タブ分けなし。絞り込みで自分の分も見られる)。
 *   一般・部門長は自分の申請のみ
 * - 表示項目: 管理番号/申請者/会社/事業部/科目①・②/金額/支払先/摘要/申請日/確認日/
 *   計上日/支払予定日・支払日/支払者/支払方法/状態/詳細(2026-08-24 指定)
 * - フィルター: ステータス・申請者・会社・事業部(チェックボックスの複数選択)
 * - 並べ替え: プルダウンで項目と昇順/降順(既定は申請日の新しい順)
 * - 横スクロールなし: 表は画面幅に収め、長い文字は「…」で切る(ホバーで全文)
 * - 「自分で支払う」を選んだ自分の申請は、支払い待ちになると「支払い済みにする」が出る
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { useToast } from '@core/components/Toast';
import { todayStr, toShortDate, tsToDateLabel, tsToMillis, tsToShortDate } from '@core/lib/date';
import { formatYen } from '@core/lib/money';
import { fetchAllApplications, fetchMyAndDeptApplications, fetchMyApplications, fetchRecurringForList } from './applicationRepo';

/** 種別(フィルター・CSV用)。繰り返し支払いも申請一覧に出す(2026-09-08 指示) */
const kindOf = (t: Transaction): 'expense' | 'report' | 'recurring' =>
  t.source === 'report' ? 'report' : t.source === 'recurring' ? 'recurring' : 'expense';
const KIND_LABELS = { expense: '経費申請', report: '支払い済み申請', recurring: '繰り返し支払い' } as const;
import { fetchUsers } from '@core/masters/masterRepo';
import { markReadForTransaction, subscribeMyUnread } from './notificationsRepo';
import { CANCELLABLE_STATUSES, cancelApplication } from './flowActions';
import { isFlowStaleError, reportFlowResult } from './flowResult';
import { deleteDraft, listDrafts, type DraftItem } from './draftRepo';
import { StatusBadge, ReceiptAttachModal } from './FlowParts';
import ApplicationDetailModal from './ApplicationDetailModal';
import MultiSelectFilter from '@core/components/MultiSelectFilter';
import { SheetTh, applySheet, countSheetFilters, type SheetColumn, type SheetFilters, type SheetSort } from '@core/components/SheetHeader';
import { useIsMobile } from '@core/lib/useIsMobile';
import {
  PAYER_LABELS,
  PAYMENT_METHOD_LABELS,
  STATUS_LABELS,
  TRANSACTION_STATUSES,
  detailedStatusLabel,
  type Transaction,
} from '@core/lib/types';

/** 並べ替え項目(表の外のプルダウンで選ぶ) */
const SORT_OPTIONS = [
  { key: 'applied', label: '申請日' },
  { key: 'amount', label: '金額' },
  { key: 'payment', label: '支払日' },
  { key: 'status', label: '状態' },
] as const;
type SortKey = (typeof SORT_OPTIONS)[number]['key'];

/**
 * 期日アラート(v3.8 2-5)。
 * 申請の遅れ: 申請日が支払い予定日の前日=黄 / 当日以降=赤
 * 支払いの遅れ: 未払いのまま今日が支払い予定日の前日=黄 / 当日以降=赤
 */
const YELLOW_BG: React.CSSProperties = { background: 'var(--amber-bg)' };
const RED_BG: React.CSSProperties = { background: 'var(--red-bg)' };

function dayDiff(a: string, b: string): number {
  return Math.round((new Date(a).getTime() - new Date(b).getTime()) / 86400000);
}

/** 申請日セルの色: 支払い予定日に対して申請が遅い場合 */
function appliedAlertStyle(t: Transaction): React.CSSProperties | undefined {
  const applied = t.applied_at as { toDate?: () => Date } | null;
  if (!applied?.toDate || !t.payment_date) return undefined;
  const d = applied.toDate();
  const appliedDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const diff = dayDiff(t.payment_date, appliedDate);
  if (diff <= 0) return RED_BG;
  if (diff === 1) return YELLOW_BG;
  return undefined;
}

/** 支払い予定日セルの色: 未払いのまま期日が迫っている/過ぎている場合 */
function paymentAlertStyle(t: Transaction, today: string): React.CSSProperties | undefined {
  if (t.paid_at || t.status === 'rejected' || t.status === 'cancelled' || !t.payment_date) return undefined;
  const diff = dayDiff(t.payment_date, today);
  if (diff <= 0) return RED_BG;
  if (diff === 1) return YELLOW_BG;
  return undefined;
}

/** ページを行き来したとき前回の内容を即表示するためのキャッシュ(2026-08-28)。
    まずキャッシュを描画し、裏で最新を取り直して置き換える(読み込み待ちをなくす) */
const listCache = new Map<
  string,
  { rows: Transaction[]; drafts: DraftItem[]; userNames: Map<string, string> }
>();

/** 開いている間、この間隔で一覧を読み直す(2026-09-17 改修依頼 B: ほかの人の差戻し・取消が開き直すまで出なかった) */
const AUTO_REFRESH_MS = 60_000;

/** 進み具合の初期値。取消は「表示し、初期状態では絞り込みで隠す」(2026-09-17 発注者指示) */
const DEFAULT_PHASES = ['active', 'done'];

export default function MyApplicationsPage() {
  const { user, can, isAccounting, flowRole } = useAuth();
  const { accountName, subAccountName, departmentName, departments, companies, companyName, accounts } = useMasters();
  const toast = useToast();
  const navigate = useNavigate();

  /** 管理者(master_admin)と経理は全申請を1つの一覧で確認できる(2026-08-24 決定) */
  const seesAll = can('master_admin') || isAccounting;
  /** 部門長は自分+担当事業部の申請(部下の分)を確認できる(2026-08-24) */
  const isBucho = flowRole === 'bucho' && (user?.manager_department_ids.length ?? 0) > 0;

  const cached = user ? listCache.get(user.id) : undefined;
  const [rows, setRows] = useState<Transaction[]>(cached?.rows ?? []);
  /** 経費申請の下書き。一覧の先頭に「下書き」行として表示(2026-08-26)。複数持てる(2026-09-17 改修依頼 A) */
  const [drafts, setDrafts] = useState<DraftItem[]>(cached?.drafts ?? []);
  /** 下書きを読み込めなかった(「下書きがない」と区別して知らせる) */
  const [draftsFailed, setDraftsFailed] = useState(false);
  const [userNames, setUserNames] = useState<Map<string, string>>(cached?.userNames ?? new Map());
  const [attaching, setAttaching] = useState<{ t: Transaction; mode: 'receive' | 'settle' } | null>(null);
  const [detail, setDetail] = useState<Transaction | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('applied');
  const [sortDesc, setSortDesc] = useState(true);
  /**
   * PCの表は見出しで並べ替え・絞り込み(2026-10-14 発注者指示: スプレッドシートのように)。
   * 表の上の欄は「進み具合」「種別」(表の列に無いもの)だけを残す。スマホはカード表示のため従来の欄で操作する
   */
  const [sheetFilters, setSheetFilters] = useState<SheetFilters>({});
  const [sheetSort, setSheetSort] = useState<SheetSort>({ key: 'applied', dir: 'desc' });
  /** 上部チップの絞り込み(■17 第1弾・2026-08-31): 進行中/完了・経費/承認不要 */
  /** 進み具合(進行中/完了/取消)と種別(経費申請/承認不要)。空=すべて。進み具合の初期値は取消以外 */
  const [fPhase, setFPhase] = useState<string[]>(DEFAULT_PHASES);
  const [fKind, setFKind] = useState<string[]>([]);
  // フィルター(複数選択。空=すべて)
  const [fStatus, setFStatus] = useState<string[]>([]);
  const [fCreator, setFCreator] = useState<string[]>([]);
  const [fCompany, setFCompany] = useState<string[]>([]);
  const [fDept, setFDept] = useState<string[]>([]);
  /** 「読み込み中…」は初回(キャッシュなし)だけ。再読込は画面を保ったまま裏で行う(2026-08-28) */
  const [loading, setLoading] = useState(!cached);
  /** スマホはC案: 申請者/会社/事業部/科目①/金額/申請日/支払い者/支払い方法/状態/詳細 の10列に絞る(2026-08-28 選定) */
  const isMobile = useIsMobile();
  /** スマホの申請日セルは幅節約のため曜日を省略(例: 08/27(木)→08/27) */
  const shortDate = (s: string) => (isMobile ? s.replace(/\(.\)$/, '') : s);

  /**
   * 自分のアクションが必要な申請か(2026-08-28 指示)。
   * 該当行は下書きと同じ黄色地で強調する(再申請・受領・支払い・精算・確認など役割別に判定)
   */
  function needsMyAction(t: Transaction): boolean {
    if (!user) return false;
    if (t.status === 'cancelled' || t.status === 'completed') return false;
    const mine = t.created_by === user.id;
    // 申請者本人: 再申請 / 仮払い金の受領 / 自分の支払い
    if (mine && t.status === 'rejected') return true;
    if (mine && t.payer === 'self' && t.status === 'cash_ready') return true;
    if (
      mine &&
      t.payer === 'self' &&
      (t.status === 'received' ||
        (t.status === 'approved' && t.payment_method !== 'prepaid' && t.payment_method !== 'cash'))
    )
      return true;
    // 立替した本人: 精算後の受領書添付
    if (
      t.paid_by === user.id &&
      t.advance === true &&
      t.status === 'paid' &&
      t.reimbursed_at != null &&
      t.settlement_received_at == null
    )
      return true;
    // 部門長: 部門長確認 / 部門長支払い
    const isBuchoOf = flowRole === 'bucho' && (user.manager_department_ids ?? []).includes(t.department_id);
    if (isBuchoOf && t.status === 'applied') return true;
    if (isBuchoOf && t.payer === 'bucho' && t.status === 'approved') return true;
    // 承認者: 承認 / 完了確認(報告型は経理確認後)
    if (flowRole === 'approver') {
      if (t.status === 'manager_checked' && t.created_by !== user.id) return true;
      if (t.status === 'paid') {
        if (t.source === 'report') return !!t.report_accounting_confirmed_at;
        if (!(t.advance === true && (!t.reimbursed_at || !t.settlement_received_at))) return true;
      }
    }
    // 経理: 経理支払い / 仮払い / 立替精算 / 報告型の経理確認
    if (flowRole === 'accounting') {
      if (t.payer === 'accounting' && t.status === 'approved') return true;
      if (t.payment_method === 'prepaid' && t.status === 'approved') return true;
      if (t.advance === true && t.status === 'paid' && t.reimbursed_at == null) return true;
      if (t.source === 'report' && t.status === 'paid' && !t.report_accounting_confirmed_at) return true;
    }
    return false;
  }

  /**
   * 一覧の読み込み。申請・利用者名簿・下書きは別々に読み、どれかが失敗しても残りは表示する
   * (2026-09-17 改修依頼 A: 名簿の読み込みに失敗すると、下書きの行まで出ていなかった)。
   * silent=true(自動の読み直し)のときは、失敗しても通知を出さない
   */
  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!user) return;
      // 申請一覧の通知(進捗連絡・赤●)は廃止(2026-08-26)。進捗は「状態」列で確認する
      const [listRes, usersRes, draftsRes] = await Promise.allSettled([
        seesAll
          ? // 経理・承認者は繰り返し支払い(家賃・リース等)も同じ一覧で見る(2026-09-08 指示)
            Promise.all([fetchAllApplications(), fetchRecurringForList().catch(() => [] as Transaction[])]).then(
              ([a, r]) => [...a, ...r],
            )
          : isBucho
            ? fetchMyAndDeptApplications(user.id, user.manager_department_ids)
            : fetchMyApplications(user.id),
        fetchUsers(),
        listDrafts(user.id),
      ]);
      const prev = listCache.get(user.id);
      let nextRows = prev?.rows ?? [];
      let nextNames = prev?.userNames ?? new Map<string, string>();
      let nextDrafts = prev?.drafts ?? [];
      if (listRes.status === 'fulfilled') {
        nextRows = listRes.value;
        setRows(nextRows);
      } else if (!opts?.silent) {
        toast(listRes.reason instanceof Error ? listRes.reason.message : '申請一覧を取得できませんでした');
      }
      if (usersRes.status === 'fulfilled') {
        nextNames = new Map(usersRes.value.map((u) => [u.id, u.display_name]));
        setUserNames(nextNames);
      }
      if (draftsRes.status === 'fulfilled') {
        nextDrafts = draftsRes.value;
        setDrafts(nextDrafts);
        setDraftsFailed(false);
      } else {
        console.error('下書きの読み込みに失敗しました', draftsRes.reason);
        setDraftsFailed(true);
      }
      listCache.set(user.id, { rows: nextRows, drafts: nextDrafts, userNames: nextNames });
      setLoading(false);
    },
    [user, seesAll, isBucho, toast],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // お知らせ欄からの遷移(2026-08-26 会議): state.openId の申請の詳細ポップアップを自動で開く
  const location = useLocation();
  const openId = (location.state as { openId?: string } | null)?.openId ?? null;
  const [openedFromNews, setOpenedFromNews] = useState(false);
  useEffect(() => {
    if (!openId || openedFromNews || rows.length === 0) return;
    const t = rows.find((r) => r.id === openId);
    if (t) {
      setDetail(t);
      setOpenedFromNews(true);
    }
  }, [openId, openedFromNews, rows]);

  // 通知が届いたら一覧も自動で最新化する(2026-08-26。ページの再読込は不要)。
  // タブに戻ってきたときも念のため読み直す。
  // 2026-09-17 改修依頼 B: 自分に通知が来ない変更(ほかの人の取消など)は開き直すまで出なかったため、
  // 別のウィンドウから戻ったときと、開いている間は1分ごとにも読み直す
  useEffect(() => {
    if (!user) return;
    let first = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastRefresh = Date.now();
    const refresh = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        lastRefresh = Date.now();
        void load({ silent: true });
      }, 800);
    };
    const unsubscribe = subscribeMyUnread(user.id, () => {
      if (first) {
        first = false;
        return;
      }
      refresh();
    });
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    // ウィンドウの切り替えで何度も読まないよう、直前の読み直しから15秒はあける
    const onFocus = () => {
      if (Date.now() - lastRefresh > 15_000) refresh();
    };
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') refresh();
    }, AUTO_REFRESH_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
      if (timer) clearTimeout(timer);
    };
  }, [user, load]);


  /** 申請者フィルターの候補: 先頭に「自分」、続けて一覧に登場する人 */
  const creatorOptions = useMemo(() => {
    const ids = [...new Set(rows.map((t) => t.created_by))].filter((id) => id !== user?.id);
    const others = ids
      .map((id) => ({ value: id, label: userNames.get(id) ?? id }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ja'));
    return user ? [{ value: user.id, label: '自分' }, ...others] : others;
  }, [rows, userNames, user]);

  const anyFilter =
    fPhase.length + fKind.length + fStatus.length + fCreator.length + fCompany.length + fDept.length > 0;

  /** 表の列(PCの見出しで並べ替え・絞り込みに使う)。表示と同じ文字で絞り込む */
  const listColumns = useMemo<SheetColumn<Transaction>[]>(
    () => [
      { key: 'creator', label: '申請者', text: (t) => (t.source === 'recurring' ? '繰り返し支払い' : (userNames.get(t.created_by) ?? '—')) },
      { key: 'company', label: '会社', text: (t) => companyName(t.company_id ?? 'sum') },
      { key: 'dept', label: '事業部', text: (t) => departmentName(t.department_id) },
      { key: 'account', label: '科目①', text: (t) => (t.account_id ? accountName(t.account_id) : '(経理入力)') },
      { key: 'sub', label: '科目②', text: (t) => (t.sub_account_id ? subAccountName(t.sub_account_id) : '—') },
      { key: 'amount', label: '金額', text: (t) => formatYen(t.amount), sortValue: (t) => t.amount },
      { key: 'applied', label: '申請日', text: (t) => tsToShortDate(t.applied_at), sortValue: (t) => tsToMillis(t.applied_at) },
      { key: 'checked', label: '部門長承認日', text: (t) => tsToShortDate(t.manager_checked_at), sortValue: (t) => tsToMillis(t.manager_checked_at) },
      { key: 'due', label: '支払予定日', text: (t) => (t.paid_at ? '—' : toShortDate(t.payment_date)), sortValue: (t) => (t.paid_at ? '' : t.payment_date) },
      { key: 'paid', label: '支払日', text: (t) => (t.paid_at ? toShortDate(t.payment_date) : ''), sortValue: (t) => (t.paid_at ? t.payment_date : '') },
      { key: 'payer', label: '支払者', text: (t) => (t.payer ? PAYER_LABELS[t.payer] : '—') },
      { key: 'method', label: '支払方法', text: (t) => (t.payment_method ? PAYMENT_METHOD_LABELS[t.payment_method] : '—') },
      {
        key: 'status',
        label: '状態',
        text: (t) =>
          detailedStatusLabel({
            payment_method: t.payment_method ?? null,
            payer: t.payer ?? null,
            advance: t.advance,
            reimbursed_at: t.reimbursed_at ?? null,
            settlement_received_at: t.settlement_received_at ?? null,
            status: t.status,
            doc_type: t.doc_type,
            source: t.source,
            report_accounting_confirmed_at: t.report_accounting_confirmed_at ?? null,
          }),
      },
    ],
    [userNames, companyName, departmentName, accountName, subAccountName],
  );

  /** 表の上の欄による絞り込み。PCは「進み具合」「種別」だけ(ほかは見出しで行う) */
  const baseRows = useMemo(
    () =>
      rows.filter(
        (t) =>
          // 進み具合(進行中/完了/取消)と種別(経費申請/承認不要)。2026-08-31 プルダウンに統一
          // ステータスで明示的に選んだもの(例: 取消)は、進み具合の絞り込みより優先して表示する(スマホ)
          (fPhase.length === 0 ||
            fPhase.includes(
              t.status === 'completed' ? 'done' : t.status === 'cancelled' ? 'cancelled' : 'active',
            ) ||
            (isMobile && fStatus.includes(t.status))) &&
          (fKind.length === 0 || fKind.includes(kindOf(t))) &&
          (!isMobile ||
            ((fStatus.length === 0 || fStatus.includes(t.status)) &&
              (fCreator.length === 0 || fCreator.includes(t.created_by)) &&
              (fCompany.length === 0 || fCompany.includes(t.company_id ?? 'sum')) &&
              (fDept.length === 0 || fDept.includes(t.department_id)))),
      ),
    [rows, isMobile, fPhase, fKind, fStatus, fCreator, fCompany, fDept],
  );

  const visible = useMemo(() => {
    // PC: 見出しの絞り込み・並べ替え(2026-10-14)
    if (!isMobile) return applySheet(baseRows, listColumns, sheetFilters, sheetSort);
    const filtered = baseRows.filter(
      (t) =>
        // 進み具合(進行中/完了/取消)と種別(経費申請/承認不要)。2026-08-31 プルダウンに統一
        // ステータスで明示的に選んだもの(例: 取消)は、進み具合の絞り込みより優先して表示する
        !!t,
    );
    const dir = sortDesc ? -1 : 1;
    const value = (t: Transaction): string | number => {
      switch (sortKey) {
        case 'applied':
          return tsToMillis(t.applied_at);
        case 'payment':
          return t.payment_date;
        case 'amount':
          return t.amount;
        case 'status':
          return STATUS_LABELS[t.status];
      }
    };
    return filtered.sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      return (va < vb ? -1 : va > vb ? 1 : 0) * dir;
    });
  }, [baseRows, isMobile, listColumns, sheetFilters, sheetSort, sortKey, sortDesc]);

  /** 念のため、その申請宛ての未読(操作依頼など)があれば既読にする */
  function clearUnread(t: Transaction) {
    if (!user) return;
    void markReadForTransaction(user.id, t.id);
  }

  function openDetail(t: Transaction) {
    clearUnread(t);
    setDetail(t);
  }

  function resubmit(t: Transaction) {
    clearUnread(t);
    navigate('/apply', { state: { transaction: t } });
  }

  /** 仮払いの受領・立替精算の受領書添付(いずれも記入済み受領書の添付が必須・v1.1) */
  function openAttach(t: Transaction, mode: 'receive' | 'settle') {
    clearUnread(t);
    setAttaching({ t, mode });
  }

  /** 下書きの削除(下書きのみ削除可。申請済みは取消のみ・2026-08-26)。失敗したら知らせる(2026-09-17) */
  async function removeDraft(d: DraftItem) {
    if (!user) return;
    const ok = window.confirm('下書きを削除しますか?(削除した下書きは元に戻せません)');
    if (!ok) return;
    try {
      await deleteDraft(user.id, d.id);
      setDrafts((prev) => prev.filter((x) => x.id !== d.id));
      toast('下書きを削除しました');
    } catch (e) {
      console.error('下書きの削除に失敗しました', e);
      toast('下書きを削除できませんでした。通信状態を確認して、もう一度お試しください');
    }
  }

  function continueDraft(d: DraftItem) {
    navigate(`/apply?draft=${encodeURIComponent(d.id)}`);
  }

  /**
   * CSV出力(2026-08-31・承認者のみ)。全社PL・全社CFへの反映内容を精査するため、
   * 一覧に出ている申請を1行1件で書き出す。項目は「経費申請から取得できる数字」の全量。
   * Excelでそのまま開けるよう BOM 付き UTF-8、金額は数値のまま出す
   */
  function exportCsv() {
    const head = [
      '管理番号', '種別', '状態',
      '会社', '事業部', '拠点/場所',
      '科目①コード', '科目①', '科目②', 'PL区分',
      '計上日', '計上月', '購入日(クレカ)', '支払日', '支払月', '支払予定日',
      '申請金額', '確定金額', '差額', '振込手数料',
      '支払い方法', '支払い者', '支払口座', '立替', '支払先', '摘要', '備考', '承認者備考',
      '申請者', '申請日時', '部門長確認者', '部門長確認日時', '承認者', '承認日時',
      '支払者', '支払日時', '経理確認者', '経理確認日時', '完了確認者', '完了日時',
      '取消者', '取消日時', '差戻し回数', '直近の差戻し理由',
      '添付枚数', '証憑枚数',
    ];
    const q = (v: unknown) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
    };
    const name = (id: string | null | undefined) => (id ? (userNames.get(id) ?? id) : '');
    const acc = (id: string) => accounts.find((a) => a.id === id);
    const lines = [head.join(',')];
    for (const t of visible) {
      const a = acc(t.account_id);
      const rej = t.reject_history ?? [];
      lines.push(
        [
          t.code ?? '', KIND_LABELS[kindOf(t)], STATUS_LABELS[t.status],
          companyName(t.company_id ?? 'sum'), departmentName(t.department_id),
          t.hierarchy_node_id ?? '',
          a?.code ?? '', a?.name ?? '', t.sub_account_id ? subAccountName(t.sub_account_id) : '',
          a?.pl_line ?? '',
          t.posting_date, t.posting_month, t.purchase_date ?? '', t.paid_at ? t.payment_date : '', t.paid_at ? t.payment_month : '', t.payment_date,
          t.applied_amount ?? '', t.paid_at ? t.amount : '',
          t.paid_at && t.applied_amount != null ? t.amount - t.applied_amount : '',
          t.transfer_fee ?? '',
          t.payment_method ? PAYMENT_METHOD_LABELS[t.payment_method] : '',
          t.payer ? PAYER_LABELS[t.payer] : '', t.payment_account_id ?? '',
          t.advance === true ? '立替' : '',
          t.counterparty ?? '', t.description ?? '', t.note ?? '', t.approver_note ?? '',
          name(t.created_by), tsToDateLabel(t.applied_at),
          name(t.manager_checked_by), tsToDateLabel(t.manager_checked_at),
          name(t.approved_by), tsToDateLabel(t.approved_at),
          name(t.paid_by), tsToDateLabel(t.paid_at),
          name(t.report_accounting_confirmed_by), tsToDateLabel(t.report_accounting_confirmed_at),
          name(t.completed_by), tsToDateLabel(t.completed_at),
          name(t.cancelled_by), tsToDateLabel(t.cancelled_at),
          rej.length, rej[rej.length - 1]?.reason ?? '',
          (t.attachments ?? []).length, (t.receipts ?? []).length,
        ].map(q).join(','),
      );
    }
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a2 = document.createElement('a');
    a2.href = url;
    a2.download = `申請一覧_${todayStr()}.csv`;
    a2.click();
    URL.revokeObjectURL(url);
    toast(`${visible.length}件をCSVに書き出しました`);
  }

  /** 申請の取消(2026-08-26)。削除せず「取消」として履歴に残す */
  async function cancel(t: Transaction) {
    if (!user) return;
    const ok = window.confirm(
      `${t.code} を取り消しますか?\n取り消した申請は削除されず、履歴に「取消」として残ります。`,
    );
    if (!ok) return;
    try {
      const result = await cancelApplication({ transaction: t, userId: user.id });
      reportFlowResult(
        toast,
        `${t.code} を取り消しました(履歴に残ります。進み具合で「取消」を選ぶと表示されます)`,
        result,
      );
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '取消に失敗しました');
      // ほかの人が先に処理していた場合は、一覧を最新にする
      if (isFlowStaleError(e)) await load();
    }
  }

  const today = todayStr();

  return (
    <section className="screen" style={{ maxWidth: 1360 }}>
      {/* スマホは外枠なしのフラット表示(2026-08-28 指示) */}
      <div className={isMobile ? 'card mylist-flat' : 'card'}>
        {/* 見出し〜並べ替えまでは画面上部に固定し、一覧だけをスクロールさせる(2026-08-31 指示) */}
        <div className="list-head">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          申請一覧{seesAll ? <span className="hint" style={{ fontWeight: 400 }}>(全事業部)</span> : isBucho ? <span className="hint" style={{ fontWeight: 400 }}>(自分+担当事業部)</span> : null}
          {/* CSV出力は承認者のみ(2026-08-31 指示。全社PL・CFへの反映内容を精査するため) */}
          {flowRole === 'approver' && (
            <button className="txn-link" style={{ marginLeft: 'auto', fontWeight: 400 }} onClick={exportCsv}>
              CSV出力
            </button>
          )}
        </h3>

        {/* 絞り込みはプルダウンに統一(2026-08-31 指示。上部チップとの重複を解消)。
            絞り込み行・並べ替え行に分け、それぞれ左詰め(2026-08-28 指示) */}
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <span className="tool-filter">
            <span className="hint">絞り込み</span>
            <MultiSelectFilter
              label="進み具合"
              options={[
                { value: 'active', label: '進行中' },
                { value: 'done', label: '完了' },
                { value: 'cancelled', label: '取消' },
              ]}
              selected={fPhase}
              onChange={setFPhase}
            />
            <MultiSelectFilter
              label="種別"
              options={[
                { value: 'expense', label: '経費申請' },
                { value: 'report', label: '支払い済み申請(承認不要)' },
                ...(seesAll ? [{ value: 'recurring', label: '繰り返し支払い' }] : []),
              ]}
              selected={fKind}
              onChange={setFKind}
            />
            {isMobile && (
            <MultiSelectFilter
              label="ステータス"
              options={TRANSACTION_STATUSES.map((s) => ({ value: s, label: STATUS_LABELS[s] }))}
              selected={fStatus}
              onChange={setFStatus}
            />
            )}
            {isMobile && (seesAll || isBucho) && (
              <MultiSelectFilter label="申請者" options={creatorOptions} selected={fCreator} onChange={setFCreator} />
            )}
            {isMobile && (
            <MultiSelectFilter
              label="会社"
              options={companies.map((c) => ({ value: c.id, label: c.name }))}
              selected={fCompany}
              onChange={setFCompany}
            />
            )}
            {isMobile && (
            <MultiSelectFilter
              label="事業部"
              options={departments.map((d) => ({ value: d.id, label: d.name }))}
              selected={fDept}
              onChange={setFDept}
            />
            )}
            {anyFilter && (
              <button
                className="txn-link"
                onClick={() => {
                  setFPhase([]);
                  setFKind([]);
                  setFStatus([]);
                  setFCreator([]);
                  setFCompany([]);
                  setFDept([]);
                }}
              >
                すべて解除
              </button>
            )}
          </span>
          {!isMobile && (
            <span className="tool-sort">
              <span className="hint">表の見出し(項目名)を押すと、並べ替えと絞り込みができます</span>
              {countSheetFilters(sheetFilters) > 0 && (
                <button className="txn-link" onClick={() => setSheetFilters({})}>
                  見出しの絞り込みをすべて解除({countSheetFilters(sheetFilters)}列)
                </button>
              )}
            </span>
          )}
          {isMobile && (
          <span className="tool-sort">
            <span className="hint">並べ替え</span>
            <select value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)}>
              {SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
            <select value={sortDesc ? 'desc' : 'asc'} onChange={(e) => setSortDesc(e.target.value === 'desc')}>
              <option value="desc">新しい順 / 大きい順</option>
              <option value="asc">古い順 / 小さい順</option>
            </select>
          </span>
          )}
        </div>
        </div>

        {draftsFailed && (
          <p className="hint" style={{ color: 'var(--red)', margin: '0 0 8px' }}>
            下書きを読み込めませんでした。1分ほどで自動的に読み直します。
          </p>
        )}
        {loading ? (
          <p className="empty">読み込み中…</p>
        ) : visible.length === 0 && drafts.length === 0 ? (
          <p className="empty">{rows.length === 0 ? '申請はまだありません。' : '条件に合う申請がありません。'}</p>
        ) : isMobile ? (
          /* スマホ(2026-08-28 指示): 銀行明細風の2段リスト・外枠なし・行タップで詳細。
             1段目=日付・会社・事業部・状態 / 2段目=申請者・科目・支払方法+金額(大) */
          <div>
            {drafts.map((draft) => (
              <div
                key={`draft-${draft.id}`}
                style={{ borderBottom: '1px solid var(--line)', padding: '10px 4px', background: 'var(--amber-bg)' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--muted)' }}>
                  <span>{shortDate(tsToShortDate(draft.updated_at))}</span>
                  <span>
                    {draft.companyId ? companyName(draft.companyId) : ''}
                    {draft.departmentId ? `・${departmentName(draft.departmentId)}` : ''}
                  </span>
                  <span className="badge b-plan" style={{ marginLeft: 'auto' }}>下書き</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                  <span style={{ fontSize: 13, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <b>{user?.display_name ?? '自分'}</b> {draft.accountId ? accountName(draft.accountId) : ''}
                    {draft.amountText ? ` ${draft.amountText}円` : ''}
                  </span>
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 14, flexShrink: 0 }}>
                    <button className="txn-link" style={{ fontWeight: 600 }} onClick={() => continueDraft(draft)}>
                      続きを入力
                    </button>
                    <button className="txn-link" style={{ color: 'var(--red)' }} onClick={() => void removeDraft(draft)}>
                      削除
                    </button>
                  </span>
                </div>
              </div>
            ))}
            {visible.map((t) => {
              const canReceive = t.created_by === user?.id && t.payer === 'self' && t.status === 'cash_ready';
              const canSettle =
                t.paid_by === user?.id &&
                t.advance === true &&
                t.status === 'paid' &&
                t.reimbursed_at != null &&
                t.settlement_received_at == null;
              return (
                <div
                  key={t.id}
                  onClick={() => openDetail(t)}
                  style={{
                    borderBottom: '1px solid var(--line)',
                    padding: '10px 4px',
                    cursor: 'pointer',
                    background: needsMyAction(t) ? 'var(--amber-bg)' : undefined,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--muted)' }}>
                    <span>{shortDate(tsToShortDate(t.applied_at))}</span>
                    <span>
                      {companyName(t.company_id ?? 'sum')}・{departmentName(t.department_id)}
                    </span>
                    <span style={{ marginLeft: 'auto' }}>
                      <StatusBadge t={t} />
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 4 }}>
                    <span
                      style={{ fontSize: 13, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      <b>{userNames.get(t.created_by) ?? '—'}</b>{' '}
                      {t.account_id ? accountName(t.account_id) : '(経理入力)'}
                      {t.payment_method ? `・${PAYMENT_METHOD_LABELS[t.payment_method]}` : ''}
                    </span>
                    <span style={{ marginLeft: 'auto', fontSize: 16, fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {/* 金額差異はPCと同様に申請時の金額を()で併記(2026-08-28 指示。多い場合のみ赤字) */}
                      {t.paid_at != null && t.applied_amount != null && t.applied_amount !== t.amount && (
                        <span
                          style={{
                            fontSize: 11.5,
                            fontWeight: 400,
                            color: t.amount > t.applied_amount ? 'var(--red)' : undefined,
                            marginRight: 4,
                          }}
                        >
                          ({formatYen(t.applied_amount)})
                        </span>
                      )}
                      {formatYen(t.amount)}円 <span style={{ color: 'var(--muted)', fontWeight: 400 }}>›</span>
                    </span>
                  </div>
                  {(canReceive || canSettle) && (
                    <div style={{ marginTop: 4, textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      {canReceive && (
                        <button className="txn-link" style={{ color: 'var(--green)', fontWeight: 600 }} onClick={() => openAttach(t, 'receive')}>
                          受領を記録
                        </button>
                      )}
                      {canSettle && (
                        <button className="txn-link" style={{ color: 'var(--green)', fontWeight: 600 }} onClick={() => openAttach(t, 'settle')}>
                          精算受領書を添付
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <table className="tx-table">
            {/* 2-3指定の列構成。管理番号・種別(doc_type)・計上日・支払先・摘要は非表示
                (DBには保持。詳細ポップアップで確認できる。種別は将来復元の可能性あり) */}
            {/* 金額列を広げて1段表示でも省略されないようにする(2026-08-26) */}
            {/* 曜日併記(■13)に合わせて日付4列を広げ、見切れを解消(■15・2026-08-28) */}
            {/* スマホ(C案・2026-08-28 選定): 科目②・部門長承認日・支払予定日・支払日を省き、
                申請者/会社/事業部/科目①/金額/申請日/支払い者/支払い方法/状態/詳細 の10列に */}
            {isMobile ? (
              <colgroup>
                <col style={{ width: '10.5%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '8%' }} />
              </colgroup>
            ) : (
              <colgroup>
                {/* 見出しに ▾ が付いた分、短い見出しが「…」で切れないよう配分し直した(2026-10-15)。詳細の列から回す */}
                <col style={{ width: '6.5%' }} />
                <col style={{ width: '4.5%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '6.5%' }} />
                <col style={{ width: '5.5%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '7.5%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '5.5%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '10.5%' }} />
                <col style={{ width: '7%' }} />
              </colgroup>
            )}
            <thead>
              <tr>
                {/* 見出しで並べ替え・絞り込み(2026-10-14)。表はPCのみ(スマホはカード表示) */}
                {listColumns.map((c) => (
                  <SheetTh
                    key={c.key}
                    column={c}
                    columns={listColumns}
                    rows={baseRows}
                    filters={sheetFilters}
                    onFilters={setSheetFilters}
                    sort={sheetSort}
                    onSort={setSheetSort}
                  />
                ))}
                <th>詳細</th>
              </tr>
            </thead>
            <tbody>
              {/* 下書き行(常に先頭)。「続きを入力」で経費申請画面に復元、下書きのみ削除できる(2026-08-26)。
                  複数の下書きを新しい順に並べる(2026-09-17 改修依頼 A) */}
              {drafts.map((draft) => (
                <tr key={`draft-${draft.id}`} style={{ background: 'var(--amber-bg)' }}>
                  <td>{user?.display_name ?? '自分'}</td>
                  <td className="hint">{draft.companyId ? companyName(draft.companyId) : '—'}</td>
                  <td>{draft.departmentId ? departmentName(draft.departmentId) : '—'}</td>
                  <td>{draft.accountId ? accountName(draft.accountId) : '—'}</td>
                  {!isMobile && <td>{draft.subAccountId ? subAccountName(draft.subAccountId) : '—'}</td>}
                  <td className="num">{draft.amountText || '—'}</td>
                  <td className="hint" title="下書きの保存日時">
                    {shortDate(tsToShortDate(draft.updated_at))}
                  </td>
                  {!isMobile && <td />}
                  {!isMobile && <td>{draft.paymentDate ? toShortDate(draft.paymentDate) : ''}</td>}
                  {!isMobile && <td />}
                  <td>{draft.payer ? (PAYER_LABELS as Record<string, string>)[draft.payer] ?? '—' : '—'}</td>
                  <td>{draft.paymentMethod ? (PAYMENT_METHOD_LABELS as Record<string, string>)[draft.paymentMethod] ?? '—' : '—'}</td>
                  {/* 「削除」は状態の欄に置く(2026-09-17: 詳細の欄が狭く「続きを入力」の後ろで切れて押せなかった)。
                      幅の狭いPC画面では、切らずに2行に折り返して押せるようにする */}
                  <td style={{ whiteSpace: 'normal', overflow: 'visible' }}>
                    <span className="badge b-plan">下書き</span>{' '}
                    <button className="txn-link" style={{ color: 'var(--red)' }} onClick={() => void removeDraft(draft)}>
                      削除
                    </button>
                  </td>
                  {/* 右端の欄なので、はみ出しても隣と重ならない。「続きを入力」は途中で改行しない */}
                  <td style={{ overflow: 'visible' }}>
                    <button className="txn-link" style={{ fontWeight: 600 }} onClick={() => continueDraft(draft)}>
                      続きを入力
                    </button>
                  </td>
                </tr>
              ))}
              {visible.map((t) => {
                // 未処理(まだ進んでいない)の日付セルは赤背景(2-4)。差戻し・取消は対象外
                const pendingBg = t.status === 'rejected' || t.status === 'cancelled' ? undefined : RED_BG;
                const checkedStyle = t.manager_checked_at ? undefined : pendingBg;
                const paidStyle = t.paid_at ? undefined : pendingBg;
                return (
                  <tr key={t.id} style={needsMyAction(t) ? { background: 'var(--amber-bg)' } : undefined}>
                    <td
                      title={t.source === 'recurring' ? (t.description ?? '') : (userNames.get(t.created_by) ?? '')}
                      style={t.source === 'recurring' ? { whiteSpace: 'nowrap', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' } : undefined}
                    >
                      {t.source === 'recurring' ? `繰り返し支払い: ${t.description ?? ''}` : (userNames.get(t.created_by) ?? '—')}
                    </td>
                    <td className="hint" title={companyName(t.company_id ?? 'sum')}>{companyName(t.company_id ?? 'sum')}</td>
                    <td title={departmentName(t.department_id)}>{departmentName(t.department_id)}</td>
                    <td title={t.account_id ? accountName(t.account_id) : ''}>
                      {t.account_id ? accountName(t.account_id) : <span className="hint">(経理入力)</span>}
                    </td>
                    {!isMobile && (
                      <td title={t.sub_account_id ? subAccountName(t.sub_account_id) : ''}>
                        {t.sub_account_id ? subAccountName(t.sub_account_id) : '—'}
                      </td>
                    )}
                    <td className="num">
                      {/* 支払い後に金額が申請時と違う場合は【(申請前の金額)支払い後の金額】を併記(2026-08-27)。
                          多い場合のみ申請前金額を赤字、少ない場合は両方黒字。
                          スマホ(C案)は幅の都合で併記を省略し確定額のみ(詳細で確認) */}
                      {!isMobile && t.paid_at != null && t.applied_amount != null && t.applied_amount !== t.amount && (
                        <span
                          style={{
                            color: t.amount > t.applied_amount ? 'var(--red)' : undefined,
                            fontSize: 9,
                            marginRight: 3,
                          }}
                        >
                          ({formatYen(t.applied_amount)})
                        </span>
                      )}
                      {formatYen(t.amount)}
                    </td>
                    <td style={appliedAlertStyle(t)}>{shortDate(tsToShortDate(t.applied_at))}</td>
                    {!isMobile && <td style={checkedStyle}>{tsToShortDate(t.manager_checked_at)}</td>}
                    {!isMobile && (
                      <td style={paymentAlertStyle(t, today)}>{t.paid_at ? '—' : toShortDate(t.payment_date)}</td>
                    )}
                    {!isMobile && <td style={paidStyle}>{t.paid_at ? toShortDate(t.payment_date) : ''}</td>}
                    <td>
                      {t.payer ? PAYER_LABELS[t.payer] : '—'}
                      {t.advance === true && t.payer === 'bucho' && <span className="hint">(立替)</span>}
                    </td>
                    <td>{t.payment_method ? PAYMENT_METHOD_LABELS[t.payment_method] : '—'}</td>
                    <td>
                      <StatusBadge t={t} />
                      {/* 報告型は通常の申請と同じ一覧に出るため種別を明示(2026-08-27) */}
                      {/* 種別の補足は同じ行に出す(2026-09-08 指示: 2行にしない) */}
                      {t.source === 'report' && (
                        <span className="hint" style={{ marginLeft: 4, fontSize: 9.5, whiteSpace: 'nowrap' }}>支払い済み申請</span>
                      )}
                      {t.source === 'recurring' && (
                        <span className="hint" style={{ marginLeft: 4, fontSize: 9.5, whiteSpace: 'nowrap' }}>繰り返し</span>
                      )}
                    </td>
                    <td>
                      <button className="txn-link" onClick={() => openDetail(t)}>
                        詳細
                      </button>
                      {/* 仮払いは経理の仮払い→受領(受領書添付)→支払いの順(v1.1 §3-C) */}
                      {t.created_by === user?.id && t.payer === 'self' && t.status === 'cash_ready' && (
                        <button className="txn-link" style={{ color: 'var(--green)', fontWeight: 600 }} onClick={() => openAttach(t, 'receive')}>
                          受領を記録
                        </button>
                      )}
                      {/* 「支払う」は支払いページに集約(2026-08-26 指示で申請一覧からは削除) */}
                      {/* 立替型: 経理の精算後、本人が記入済み受領書を添付すると完了確認に進む(v1.1 §3-B-6) */}
                      {t.paid_by === user?.id &&
                        t.advance === true &&
                        t.status === 'paid' &&
                        t.reimbursed_at != null &&
                        t.settlement_received_at == null && (
                          <button className="txn-link" style={{ color: 'var(--green)', fontWeight: 600 }} onClick={() => openAttach(t, 'settle')}>
                            精算受領書を添付
                          </button>
                        )}
                      {/* 再申請・取消は詳細ポップアップ内に移動(2026-08-26 指示) */}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {!isMobile && (
        <p className="hint" style={{ marginTop: 10 }}>
          赤背景=まだその段階に進んでいない箇所。申請日セルの黄/赤=支払い予定日の前日/当日以降の申請(申請の遅れ)、
          支払予定日セルの黄/赤=未払いのまま前日/当日以降(支払いの遅れ)。
          管理番号・計上日・支払先・摘要は「詳細」で確認できます。
          支払いはメニューの「支払い待ち」ページから行います。金額の赤字( )は申請時の金額です。
        </p>
        )}
      </div>

      {detail && (
        <ApplicationDetailModal
          transaction={detail}
          onClose={() => setDetail(null)}
          onChanged={() => void load()}
          actions={
            // 再申請・取消は詳細から行う(2026-08-26。申請一覧の行からは削除)
            detail.created_by === user?.id &&
            (detail.status === 'rejected' ||
              (CANCELLABLE_STATUSES as readonly string[]).includes(detail.status)) ? (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {detail.status === 'rejected' && detail.doc_type !== 'invoice' && (
                  <button className="btn-sm-ok" onClick={() => resubmit(detail)}>
                    再申請する(申請画面で修正)
                  </button>
                )}
                {(CANCELLABLE_STATUSES as readonly string[]).includes(detail.status) && (
                  <button
                    className="btn-sm-ng"
                    onClick={() => {
                      void cancel(detail).then(() => setDetail(null));
                    }}
                  >
                    この申請を取り消す(履歴に「取消」で残ります)
                  </button>
                )}
              </div>
            ) : undefined
          }
        />
      )}
      {attaching && (
        <ReceiptAttachModal
          transaction={attaching.t}
          mode={attaching.mode}
          onClose={() => setAttaching(null)}
          onDone={() => void load()}
        />
      )}
    </section>
  );
}
